import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { DataTable, type Column } from '../components/DataTable';
import {
  hierarchy,
  type Campaign, type AdGroup, type Ad,
  type AdGroupDelivery, type AdGroupRowDelivery, type CampaignDelivery, type CampaignStoreDelivery,
  type ListParams,
} from '../lib/api';
import { Creative } from '../components/Creative';
import { DeliveryDayChart } from '../components/DeliveryDayChart';
import { StatusToggle } from '../components/StatusToggle';

/** Dates arrive as `YYYY-MM-DD HH:MM:SS` US Eastern wall-clock, with no zone. */
const day = (v: string | null) => (v ? v.slice(0, 10) : '—');

const money = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * A figure with its progress against target, as the legacy list shows it.
 *
 * Sails preformats these into strings — `"732 (48.80%)"` in `clicks_with_progress` — which
 * is convenient and unsortable and unstyleable. The two parts are kept apart here and
 * joined in the markup instead.
 */
/**
 * A figure with its share of the contract underneath.
 *
 * Stacked rather than inline: eleven columns of "1,234 (5.67%)" gives the eye two numbers
 * of equal weight per cell and twenty-two per row. The figure is what is being compared
 * across rows, so it gets the line; the share is context and sits under it, smaller.
 */
function WithProgress(
  { value, target, as = 'count' }: { value: number | null; target: number | null; as?: 'count' | 'money' }
) {
  if (value === null) return <span className="muted">—</span>;
  const pct = target && target > 0 ? (value / target) * 100 : null;
  return (
    <span className="stat">
      {/*
        Spend is money and counts are counts. Sails stores spend at six decimal places, so
        rendering it with the count formatter prints "3.607" in a column of dollar amounts
        — which reads as a count of three and a bit rather than as $3.61.
      */}
      <span className="stat-value num">
        {as === 'money' ? money(value) : value.toLocaleString('en-US')}
      </span>
      {pct !== null && <span className="stat-share num">{pct.toFixed(2)}%</span>}
    </span>
  );
}

/** Trim to a length that keeps a column narrow, without cutting mid-word silently. */
function truncate(text: string, max: number): string {
  return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}

/**
 * The campaign name, with its order number split back out.
 *
 * Sails stores the SO appended to the name — "OPF Immigration 2026 - Run 3 (OPF082026)" —
 * so the identifier is duplicated in every row at the same weight as the name, and the
 * name wraps to two lines to make room for it. Stripped ONLY when the parenthetical
 * matches the SO exactly: a name that happens to end in brackets for another reason keeps
 * them.
 */
function splitName(c: Campaign): string {
  const so = (c.reportSo || c.so || '').trim();
  if (!so) return c.name;
  const suffix = `(${so})`;
  return c.name.endsWith(suffix) ? c.name.slice(0, -suffix.length).trim() : c.name;
}

/**
 * The status column, reproducing the legacy indicator.
 *
 * Three states, and the middle one is the reason the column exists: ACTIVE AND NOT
 * DELIVERING is the campaign somebody needs to look at today. Active-and-delivering is
 * fine and inactive is intended; a campaign switched on and producing nothing is neither.
 * The pacing reasons are listed underneath, because "not delivering" without "budget
 * exceeded" sends staff into the ad groups to find out why.
 */
function CampaignStatus({ c, d }: { c: Campaign; d: CampaignDelivery | undefined }) {
  const active = d?.isActive ?? c.isActive;
  const delivering = d?.isDelivering;
  /*
   * Three states, and the middle one is the point: ACTIVE AND NOT DELIVERING is the
   * campaign somebody needs to look at. While delivery is still loading `delivering` is
   * undefined, and the row shows plain "Active" rather than accusing it of stopping.
   */
  const label = !active ? 'Inactive'
    : delivering === false ? 'Not delivering'
    : 'Active';
  const tone = !active ? 'off' : delivering === false ? 'warn' : 'ok';
  return (
    <div className="status-cell">
      <span className={`pill ${tone}`} title={
        d?.lastDeliveryDate ? `Last delivered ${d.lastDeliveryDate}` : undefined
      }>{label}</span>
      {d && <ClickPace d={d} />}
      {d && d.flags.length > 0 && (
        <div className="status-reasons">
          {d.flags.map((f: string) => <span key={f} className="reason">{f}</span>)}
        </div>
      )}
    </div>
  );
}

/**
 * Click pacing, small enough to live inside the status cell.
 *
 * The question the list has to answer at a glance is "which of these needs attention
 * today", and a campaign can be Active, delivering, and quietly missing its number all
 * week. The status pill cannot say that; this can.
 *
 * The bar is clicks against the CONTRACT, with a notch at today's expected position. Two
 * campaigns both showing "40%" are not comparable — one on day 3 of 30 is far ahead and
 * one on day 25 is in trouble — and the notch is what makes them comparable.
 */
function ClickPace({ d }: { d: CampaignDelivery }) {
  // Nothing contracted, or nothing expected yet: there is no pace to be off.
  if (!d.targetClicks || d.targetClicks <= 0 || d.clicksShouldBeAt === null) return null;
  if (d.pace === 'unknown') return null;

  const diff = d.clicks - d.clicksShouldBeAt;
  /*
   * The band is defined once, in `@pdmg/data-access`, and used by BOTH this badge and the
   * server-side "behind" filter — so the toggle returns exactly the rows marked behind.
   * Two definitions of the same word would drift and nobody would trust either.
   */
  // Decided by the server, so the badge and the "behind" filter cannot disagree.
  const state = d.pace;
  const word = state === 'even' ? 'on track' : state === 'ahead' ? 'ahead' : 'behind';

  const pct = (n: number) => Math.min(100, Math.max(0, (n / d.targetClicks!) * 100));

  return (
    <div
      className={`pace pace-${state}`}
      title={`${d.clicks.toLocaleString('en-US')} clicks against ${d.clicksShouldBeAt.toLocaleString('en-US')} expected today, of ${d.targetClicks.toLocaleString('en-US')} contracted`}
    >
      <div className="pace-track">
        <div className="pace-fill" style={{ width: `${pct(d.clicks)}%` }} />
        <div className="pace-mark" style={{ left: `${pct(d.clicksShouldBeAt)}%` }} />
      </div>
      <span className="pace-word">
        {word}
        {state !== 'even' && (
          <span className="pace-by"> {diff > 0 ? '+' : '−'}{int(Math.abs(diff))}</span>
        )}
      </span>
    </div>
  );
}

/**
 * The campaign list, matching rtb_ui column for column.
 *
 * Legacy order: Status, ID, Active?, Name, Client, Overall Budget, Daily Budget,
 * Impressions, Clicks, CTR%, Spend, actions.
 *
 * `Active?` is dropped as a separate column: it duplicates the status indicator standing
 * next to it, which already distinguishes the three states that matter rather than the
 * two a boolean can say.
 */
export const campaignColumns = (
  delivery: Record<string, CampaignDelivery>,
  onAction: (action: 'edit' | 'copy' | 'adgroup' | 'report', c: Campaign) => void
): Column<Campaign>[] => [
  { key: 'status', label: 'Status', width: '9rem',
    render: (c) => <CampaignStatus c={c} d={delivery[c.id]} /> },
  { key: 'id', label: 'ID', width: '7rem',
    render: (c) => <code className="tiny">{c.id.slice(-8)}</code> },
  { key: 'name', label: 'Name', sortField: 'campaign_name', width: '22rem',
    render: (c) => (
      <span className="name-cell">
        <span className="name-main">{splitName(c)}</span>
        <span className="name-ref">
          {c.reportSo || c.so}{c.io && <> · {c.io}</>}
        </span>
      </span>
    ) },
  { key: 'client', label: 'Client', width: '11rem',
    render: (c) => (
      c.clientName
        // The full name stays in the title, so truncation hides nothing that cannot be
        // recovered by hovering — and the link still goes to the client either way.
        ? <Link to={`/clients/${c.clientId}`} title={c.clientName}
                onClick={(e) => e.stopPropagation()}>{truncate(c.clientName, 20)}</Link>
        : <span className="muted">—</span>
    ) },
  { key: 'start', label: 'Start', sortField: 'start_datetime', width: '7rem',
    render: (c) => day(c.startDatetime) },
  { key: 'end', label: 'End', sortField: 'end_datetime', width: '7rem',
    render: (c) => day(c.endDatetime) },
  { key: 'overall', label: 'Overall Budget', sortField: 'overall_budget', align: 'right', width: '9rem',
    render: (c) => <span className="num">{money(delivery[c.id]?.overallBudget ?? c.overallBudget)}</span> },
  { key: 'daily', label: 'Daily Budget', align: 'right', width: '8rem',
    render: (c) => <span className="num">{money(delivery[c.id]?.dailyBudget ?? c.dailyBudget)}</span> },
  { key: 'impressions', label: 'Impressions', align: 'right', width: '10rem',
    render: (c) => <WithProgress value={delivery[c.id]?.impressions ?? null}
                                 target={delivery[c.id]?.targetImpressions ?? null} /> },
  { key: 'clicks', label: 'Clicks', align: 'right', width: '9rem',
    render: (c) => <WithProgress value={delivery[c.id]?.clicks ?? null}
                                 target={delivery[c.id]?.targetClicks ?? null} /> },
  { key: 'ctr', label: 'CTR%', align: 'right', width: '6rem',
    render: (c) => {
      /*
       * Clicks over CONTRACTED impressions (PAP-371) — the rule Sails uses in
       * `Consolidation.ctr_percent` and the one the reports use. `clicks / impressions`
       * gives a different, wrong number that looks just as plausible.
       */
      const d = delivery[c.id];
      if (!d) return <span className="muted">—</span>;
      if (!d.targetImpressions) return <span className="muted">—</span>;
      return <span className="num">{((d.clicks / d.targetImpressions) * 100).toFixed(2)}%</span>;
    } },
  { key: 'spend', label: 'Spend', align: 'right', width: '9rem',
    render: (c) => {
      const d = delivery[c.id];
      return <WithProgress value={d ? d.spend : null} target={d?.overallBudget ?? null} as="money" />;
    } },
  { key: 'more', label: '', width: '3rem',
    render: (c) => <RowMenu campaign={c} onAction={onAction} /> },
];

/** Edit, Copy, Add AdGroup, Get Report — the legacy row menu. */
function RowMenu(
  { campaign, onAction }: {
    campaign: Campaign;
    onAction: (action: 'edit' | 'copy' | 'adgroup' | 'report', c: Campaign) => void;
  }
) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    // Any click anywhere closes it, including the one that opens another row's menu.
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);

  return (
    <div className="row-menu" onClick={(e) => e.stopPropagation()}>
      <button className="row-menu-trigger" onClick={() => setOpen((v) => !v)} aria-label="Actions">⋯</button>
      {open && (
        <div className="row-menu-pop">
          <button onClick={() => onAction('edit', campaign)}>Edit</button>
          <button onClick={() => onAction('copy', campaign)}>Copy</button>
          <button onClick={() => onAction('adgroup', campaign)}>Add ad group</button>
          <button onClick={() => onAction('report', campaign)}>Get report</button>
        </div>
      )}
    </div>
  );
}

/**
 * The campaign list, shared by the campaigns screen and a client's campaigns.
 *
 * Delivery is fetched for the page that just loaded rather than for a campaign at a time:
 * one request per page instead of fifty. It is deliberately NOT awaited before the table
 * renders — the names and budgets are already there, and holding a table of 50 rows back
 * on a consolidations query makes the whole screen feel broken when Mongo is slow. The
 * metric cells show a dash until it lands.
 */
export function CampaignTable(
  { title, scope }: { title: string; scope?: { agencyId?: string; clientId?: string } }
) {
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState<Record<string, CampaignDelivery>>({});
  const [note, setNote] = useState<string | null>(null);
  /*
   * Defaults to ACTIVE, unlike the ad list, which defaults to all.
   *
   * This is the working list of what is running — 16,317 campaigns of which most are
   * finished, and opening on all of them buries the ones that matter. It is the campaign's
   * ON/OFF flag, never delivery status: a campaign that is switched on and delivering
   * nothing stays in this view, because it is exactly what somebody needs to find.
   */
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('active');
  /*
   * Behind is a SEPARATE axis from active/inactive, not a fourth value of it.
   * "Active campaigns that are behind" is the question staff actually ask; folding it into
   * the same control would make the two mutually exclusive and lose it.
   */
  const [behindOnly, setBehindOnly] = useState(false);
  /*
   * Filter by when a campaign was CREATED in the system (`date_created`), independent of when it ran.
   * Either end may be left open. Empty means no create-date filter.
   */
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');

  const agencyId = scope?.agencyId;
  const clientId = scope?.clientId;

  const load = useCallback(async (p: ListParams) => {
    const page = await hierarchy.campaigns({
      ...p, agencyId, clientId,
      status: status === 'all' ? undefined : status,
      behind: behindOnly ? true : undefined,
      createdFrom: createdFrom || undefined,
      createdTo: createdTo || undefined,
    });
    setDelivery({});
    hierarchy.campaignDelivery(page.rows.map((r) => r.id)).then(setDelivery).catch(() => {});
    return page;
  }, [agencyId, clientId, status, behindOnly, createdFrom, createdTo]);

  const onAction = useCallback((action: string, c: Campaign) => {
    if (action === 'report') { navigate(`/campaigns/${c.id}/report`); return; }
    if (action === 'edit') { navigate(`/campaigns/${c.id}/edit`); return; }
    if (action === 'adgroup') { navigate(`/campaigns/${c.id}/adgroups/new`); return; }
    // Copy (deep clone) lands with the copy-campaign endpoint; explain until then.
    setNote('Copying a campaign is coming soon — the deep-copy endpoint is the next management milestone.');
  }, [navigate]);

  return (
    <div className="wide">
      {note && (
        <div className="banner" onClick={() => setNote(null)} role="status">
          {note} <button className="link" onClick={() => setNote(null)}>Dismiss</button>
        </div>
      )}
      <div className="row" style={{ justifyContent: 'flex-end', marginBottom: '0.6rem' }}>
        <button className="button primary" onClick={() => navigate('/campaigns/new')}>New campaign</button>
      </div>
      <div className="filter-bar">
        <span className="filter-label">Show</span>
        {(['active', 'inactive', 'all'] as const).map((v) => (
          <button
            key={v}
            className={`chip${status === v ? ' chip-on' : ''}`}
            onClick={() => setStatus(v)}
          >
            {v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive'}
          </button>
        ))}
        <button
          className={`chip chip-behind${behindOnly ? ' chip-on' : ''}`}
          onClick={() => setBehindOnly((v) => !v)}
        >
          Behind on clicks
        </button>
        <span className="filter-label" style={{ marginLeft: 'auto' }}>Created</span>
        <label className="rng">from
          <input type="date" value={createdFrom} max={createdTo || undefined}
            onChange={(e) => setCreatedFrom(e.target.value)} />
        </label>
        <label className="rng">to
          <input type="date" value={createdTo} min={createdFrom || undefined}
            onChange={(e) => setCreatedTo(e.target.value)} />
        </label>
        {(createdFrom || createdTo) && (
          <button className="chip" onClick={() => { setCreatedFrom(''); setCreatedTo(''); }}>Clear</button>
        )}
      </div>

      <DataTable
        title={title}
        columns={campaignColumns(delivery, onAction)}
        load={load}
        initialSort="start_datetime"
        searchPlaceholder="Search by name, client, SO# or IO#…"
        emptyMessage={
          behindOnly
            ? `No ${status === 'all' ? '' : status + ' '}campaigns are behind on clicks.`
            : status === 'all' ? 'No campaigns.' : `No ${status} campaigns.`
        }
        onRowClick={(c) => navigate(`/campaigns/${c.id}`)}
      />
    </div>
  );
}

export function CampaignsScreen() {
  return <CampaignTable title="Campaigns" />;
}

/**
 * Ad group columns, matching `campaign-adgroups.component.ts`.
 *
 * Legacy order: Status, ID, Active?, Name, Campaign ID, Start, End, Overall Budget,
 * Daily Budget, Impressions, Clicks, CTR%, Today Spend, Overall Spend.
 *
 * Campaign ID is dropped — every row on this page belongs to the campaign in the heading —
 * and `Active?` for the same reason it went from the campaign list: the status indicator
 * beside it already says more than a boolean can.
 */
const adGroupColumns = (
  delivery: Record<string, AdGroupRowDelivery>
): Column<AdGroup>[] => [
  { key: 'status', label: 'Status', width: '9rem',
    render: (g) => {
      const d = delivery[g.id];
      const active = d?.isActive ?? g.isActive;
      // Same three states as the campaign list; undefined means still loading.
      const label = !active ? 'Inactive' : d?.isDelivering === false ? 'Not delivering' : 'Active';
      const tone = !active ? 'off' : d?.isDelivering === false ? 'warn' : 'ok';
      return (
        <div className="status-cell">
          <span className={`pill ${tone}`}>{label}</span>
          {d && d.flags.length > 0 && (
            <div className="status-reasons">
              {d.flags.map((f: string) => <span key={f} className="reason">{f}</span>)}
            </div>
          )}
        </div>
      );
    } },
  // On/off = `is_active`, which is the load-ads gate: switching it off stops the ad group and its ads
  // being sent to Redis for the impression script.
  { key: 'load', label: 'Send to Redis', width: '8rem',
    render: (g) => <StatusToggle id={g.id} kind="adgroup" initialActive={delivery[g.id]?.isActive ?? g.isActive} /> },
  { key: 'id', label: 'ID', width: '7rem',
    render: (g) => <code className="tiny">{g.id.slice(-8)}</code> },
  { key: 'name', label: 'Name', sortField: 'adgroup_name',
    render: (g) => <strong>{g.name}</strong> },
  { key: 'start', label: 'Start', sortField: 'start_date_time', width: '7rem',
    render: (g) => day(g.startDateTime) },
  { key: 'end', label: 'End', sortField: 'end_date_time', width: '7rem',
    render: (g) => day(g.endDateTime) },
  { key: 'overall', label: 'Overall Budget', sortField: 'overall_budget', align: 'right', width: '9rem',
    render: (g) => <span className="num">{money(delivery[g.id]?.overallBudget ?? g.overallBudget)}</span> },
  { key: 'daily', label: 'Daily Budget', sortField: 'daily_budget', align: 'right', width: '8rem',
    render: (g) => <span className="num">{money(delivery[g.id]?.dailyBudget ?? g.dailyBudget)}</span> },
  { key: 'impressions', label: 'Impressions', align: 'right', width: '10rem',
    render: (g) => <WithProgress value={delivery[g.id]?.impressions ?? null}
                                 target={delivery[g.id]?.targetImpressions ?? null} /> },
  { key: 'clicks', label: 'Clicks', align: 'right', width: '9rem',
    render: (g) => <WithProgress value={delivery[g.id]?.clicks ?? null}
                                 target={delivery[g.id]?.targetClicks ?? null} /> },
  { key: 'ctr', label: 'CTR%', align: 'right', width: '6rem',
    render: (g) => {
      // Clicks over CONTRACTED impressions (PAP-371), as everywhere else.
      const d = delivery[g.id];
      if (!d || !d.targetImpressions) return <span className="muted">—</span>;
      return <span className="num">{((d.clicks / d.targetImpressions) * 100).toFixed(2)}%</span>;
    } },
  { key: 'todaySpend', label: 'Today Spend', align: 'right', width: '8rem',
    render: (g) => {
      const d = delivery[g.id];
      return <span className="num">{d ? money(d.todaySpend) : <span className="muted">—</span>}</span>;
    } },
  { key: 'spend', label: 'Overall Spend', align: 'right', width: '10rem',
    render: (g) => {
      const d = delivery[g.id];
      return <WithProgress value={d ? d.spend : null} target={d?.overallBudget ?? null} as="money" />;
    } },
];

function PacingBar(
  { label, actual, target, total }: {
    label: string; actual: number; target: number | null; total: number | null;
  }
) {
  if (total === null || total <= 0) return null;

  const pct = (n: number) => Math.min(100, Math.max(0, (n / total) * 100));
  const diff = target === null ? null : actual - target;
  // A campaign is never exactly on pace; a small band avoids calling a rounding
  // difference "behind" and training people to ignore the colour.
  const tone = diff === null ? 'even'
    : Math.abs(diff) <= Math.max(1, total * 0.01) ? 'even'
    : diff > 0 ? 'ahead' : 'behind';

  return (
    <div className={`pacing-row pacing-${tone}`}>
      <span className="pacing-name">{label}</span>
      <div className="pacing-track">
        <div className="pacing-fill" style={{ width: `${pct(actual)}%` }} />
        {/* The notch is where today's target sits on the same scale, so the gap between
            it and the end of the fill IS the shortfall or the overshoot. */}
        {target !== null && (
          <div className="pacing-target" style={{ left: `${pct(target)}%` }}
               title={`Target for today: ${int(target)}`} />
        )}
      </div>
      <span className="pacing-nums">
        <strong>{int(actual)}</strong>
        <span className="muted"> of {int(total)}</span>
        {target !== null && (
          <>
            {' · '}{int(target)}{' today'}
            {diff !== null && (
              <span className="pacing-diff">
                {' '}({diff >= 0 ? '+' : '−'}{int(Math.abs(diff))})
              </span>
            )}
          </>
        )}
      </span>
    </div>
  );
}

/**
 * Pacing from OUR store, at the top of the campaign page.
 *
 * The legacy `PacingPanel` sources actuals + the "should be at today" figure from the legacy
 * `consolidations`, which is empty for a campaign whose delivery lives only in :27018 — so it shows
 * nothing to pace. This computes the same clicks/impressions pacing from the store delivery (actuals),
 * the `bid_adjustments` contract (targets), and the flight: today's target is the contract spread evenly
 * across the flight, so the gap between the fill and the notch is the shortfall/overshoot.
 */
function StorePacingPanel({ campaignId }: { campaignId: string }) {
  const [d, setD] = useState<CampaignStoreDelivery | null>(null);
  useEffect(() => {
    setD(null);
    hierarchy.campaignStoreDelivery(campaignId).then(setD).catch(() => setD(null));
  }, [campaignId]);

  // The delivery panel below surfaces load errors; here a missing store or an uncontracted campaign
  // simply has nothing to pace, so the panel stays quiet rather than showing an empty frame.
  if (!d || (d.targetImpressions === null && d.targetClicks === null)) return null;

  const startMs = Date.parse(d.from), endMs = Date.parse(d.to);
  const durationDays = Number.isFinite(startMs) && Number.isFinite(endMs)
    ? Math.max(1, Math.round((endMs - startMs) / 86_400_000) + 1) : 1;
  const elapsedDays = Math.min(durationDays, Math.max(0, Math.round((Date.now() - startMs) / 86_400_000) + 1));
  const fraction = Math.min(1, elapsedDays / durationDays);
  const remaining = durationDays - elapsedDays;
  const should = (t: number | null) => (t === null ? null : Math.round(t * fraction));

  return (
    <section className="detail pacing">
      <div className="pacing-head">
        <h3>Pacing</h3>
        <span className="tiny muted">
          {day(d.from)} → {day(d.to)} · {durationDays} day{durationDays === 1 ? '' : 's'}
          {' · '}{remaining <= 0 ? 'complete' : `${remaining} day${remaining === 1 ? '' : 's'} left`}
          {' · '}contract over flight, from our store
        </span>
      </div>
      <div className="pacing-bars">
        <PacingBar label="Impressions" actual={d.impressions} target={should(d.targetImpressions)} total={d.targetImpressions} />
        <PacingBar label="Clicks" actual={d.clicks} target={should(d.targetClicks)} total={d.targetClicks} />
      </div>
    </section>
  );
}

/**
 * What our own consolidated store says this campaign delivered.
 *
 * Deliberately separate from the pacing panel above it, which reads the LEGACY database.
 *
 * These are OUR figures, and they do not yet match a generated report: the generate worker
 * still reads the legacy consolidations. On DRHDME0226 legacy says 13,728 impressions and this
 * store 12,490. Both are labelled by source rather than one being presented as the truth,
 * because the gap is the useful thing here — at cutover generation must read this store, and a
 * difference found then would be found on a client's report.
 */
function StoreDeliveryPanel({ campaignId }: { campaignId: string }) {
  const [d, setD] = useState<CampaignStoreDelivery | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setD(null); setError(null);
    hierarchy.campaignStoreDelivery(campaignId)
      .then(setD)
      .catch((e: Error) => setError(e.message));
  }, [campaignId]);

  if (error) return <section className="detail"><h3>Delivery</h3><p className="muted">{error}</p></section>;
  if (!d) return <section className="detail"><h3>Delivery</h3><p className="muted">Reading the store…</p></section>;

  /*
   * Zero rows is not zero delivery. It means this window has not been consolidated — saying
   * "0 impressions" there would report an absence of data as a measurement.
   */
  if (d.adRowCount === 0) {
    return (
      <section className="detail">
        <h3>Delivery</h3>
        <p className="muted">
          Nothing consolidated for {d.from} – {d.to}. This reads our own store, which is built
          by the consolidation sweep; a window it has not covered has no rows rather than zero
          delivery.
        </p>
      </section>
    );
  }

  const ctr = d.targetImpressions ? (d.clicks / d.targetImpressions) * 100 : null;

  return (
    <section className="detail">
      <div className="detail-head">
        <div>
          <h3>Delivery</h3>
          <p className="sub">
            From our store, {d.from} – {d.to} · {d.adRowCount.toLocaleString('en-US')} consolidated rows
            {d.reportSo && <> · a report covers all of <code>{d.reportSo}</code>, not this campaign alone</>}
          </p>
        </div>
      </div>

      <dl className="facts">
        <div><dt>Impressions</dt><dd>{d.impressions.toLocaleString('en-US')}</dd></div>
        <div><dt>Clicks</dt><dd>{d.clicks.toLocaleString('en-US')}</dd></div>
        <div>
          <dt>Contracted</dt>
          {/* Null, never 0 — CTR divides by this, so a zero would read as a real contract. */}
          <dd>{d.targetImpressions === null ? '—' : d.targetImpressions.toLocaleString('en-US')}</dd>
        </div>
        <div><dt>CTR</dt><dd>{ctr === null ? '—' : `${ctr.toFixed(2)}%`}</dd></div>
        <div><dt>Retargeted impressions</dt><dd>{d.retargetImpressions.toLocaleString('en-US')}</dd></div>
        <div><dt>Retargeted clicks</dt><dd>{d.retargetClicks.toLocaleString('en-US')}</dd></div>
        <div><dt>Mobile clicks</dt><dd>{d.mobileClicks.toLocaleString('en-US')}</dd></div>
        <div><dt>Desktop clicks</dt><dd>{d.desktopClicks.toLocaleString('en-US')}</dd></div>
      </dl>

      {d.provisionalDays > 0 && (
        /*
         * Said rather than styled away. A day inside the settle window can still move, and a
         * figure quoted from it may not be the one a report carries later.
         */
        <p className="muted">
          {d.provisionalDays} day(s) are still provisional and may be recomputed.
        </p>
      )}

      {d.byDay.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <h4 style={{ margin: '0 0 0.35rem', fontSize: '0.9rem' }}>Impressions &amp; clicks by day</h4>
          <DeliveryDayChart byDay={d.byDay} />
        </div>
      )}
    </section>
  );
}

export function CampaignDetailScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    hierarchy.campaign(id).then(setCampaign).catch((e: Error) => setError(e.message));
  }, [id]);

  const [agDelivery, setAgDelivery] = useState<Record<string, AdGroupRowDelivery>>({});
  const loadAdGroups = useCallback(async (p: ListParams) => {
    const page = await hierarchy.campaignAdGroups(id, p);
    setAgDelivery({});
    // One request for the page, not one per row — and not awaited, so the table paints.
    hierarchy.adGroupsDelivery(page.rows.map((r) => r.id)).then(setAgDelivery).catch(() => {});
    return page;
  }, [id]);

  if (error) return <div className="banner error">{error}</div>;
  if (!campaign) return <div className="busy">Loading…</div>;

  return (
    <div className="wide">
      <nav className="crumbs"><Link to="/campaigns">Campaigns</Link> <span>/</span> <span>{campaign.name}</span></nav>

      <section className="detail">
        <div className="detail-head">
          <div>
            <h2>{campaign.name}</h2>
            <p className="sub">
              <code>{campaign.reportSo || campaign.so}</code>
              {campaign.io && <> · IO {campaign.io}</>}
              {' · '}{day(campaign.startDatetime)} – {day(campaign.endDatetime)}
            </p>
          </div>
          {/*
            * The report builder is reached from the campaign, because a report is
            * produced against a campaign. Disabled until the builder exists rather than
            * hidden, so the path is visible.
            */}
          <div className="row" style={{ gap: '0.5rem' }}>
            <Link className="button" to={`/campaigns/${campaign.id}/edit`}>Edit</Link>
            <Link className="button" to={`/campaigns/${campaign.id}/adgroups/new`}>Add ad group</Link>
            <Link className="button primary" to={`/campaigns/${campaign.id}/report`}>Report</Link>
          </div>
        </div>
        <dl className="facts">
          <div><dt>Ad groups</dt><dd>{campaign.counts?.adGroups ?? '—'}</dd></div>
          <div><dt>Ads</dt><dd>{(campaign.counts?.ads ?? 0).toLocaleString('en-US')}</dd></div>
          <div><dt>Budget</dt><dd>{campaign.overallBudget === null ? '—' : `$${campaign.overallBudget.toLocaleString('en-US')}`}</dd></div>
          <div><dt>Status</dt><dd>{campaign.status || (campaign.isActive ? 'active' : 'inactive')}</dd></div>
        </dl>
      </section>

      <StorePacingPanel campaignId={campaign.id} />

      <StoreDeliveryPanel campaignId={campaign.id} />

      <DataTable
        title="Ad groups"
        columns={adGroupColumns(agDelivery)}
        load={loadAdGroups}
        initialSort="start_date_time"
        searchPlaceholder="Search ad groups…"
        emptyMessage="This campaign has no ad groups."
        onRowClick={(g) => navigate(`/adgroups/${g.id}`)}
      />
    </div>
  );
}

const int = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : n.toLocaleString('en-US');

/**
 * Ad size, as the ad itself records it.
 *
 * Only display and DOOH creatives carry dimensions; an audio spot or a text ad has none,
 * and an em dash is the honest answer there rather than "0 x 0".
 */
function dimensions(a: Ad): string {
  const w = a.creative?.width;
  const h = a.creative?.height;
  return w && h ? `${w} × ${h}` : '—';
}

/**
 * Columns take the per-ad delivery totals, because they are fetched separately from the
 * ads themselves — the ad list is paginated and the totals are for the whole group.
 */
/**
 * The PPC source types whose deployed link is the campaign rotator, not a landing page. Mirrors the
 * ingest `PPC_SOURCE_TYPES` — kept as a local literal because the web app does not import from `apps/ingest`.
 */
const ROTATOR_TYPES = new Set(['POPUNDER', 'ADSIMILATE_EXTERNAL_LINK']);

const adColumns = (delivery: AdGroupDelivery | null): Column<Ad>[] => [
  // The creative first: for a display or DOOH ad it is the thing you are identifying.
  { key: 'creative', label: 'Creative', width: '20rem', render: (a) => <Creative ad={a} /> },
  { key: 'title', label: 'Ad', sortField: 'title',
    render: (a) => <strong>{a.title || <span className="muted">(untitled)</span>}</strong> },
  { key: 'size', label: 'Size', width: '7rem',
    render: (a) => <span className="num">{dimensions(a)}</span> },
  { key: 'type', label: 'Type', sortField: 'type', width: '9rem',
    render: (a) => <code className="type">{a.type}</code> },
  // On/off = the creative's `is_active`, the load-ads gate: off stops this creative being sent to Redis.
  { key: 'load', label: 'Send to Redis', width: '8rem',
    render: (a) => <StatusToggle id={a.id} kind="ad" initialActive={a.isActive} /> },
  {
    key: 'impr', label: 'Impressions', align: 'right', width: '7rem',
    render: (a) => {
      const d = delivery?.byAd[a.id];
      if (!delivery) return <span className="muted">…</span>;
      return d ? <span className="num">{int(d.impressions)}</span> : <span className="muted">—</span>;
    },
  },
  {
    key: 'clicks', label: 'Clicks', align: 'right', width: '6rem',
    render: (a) => {
      const d = delivery?.byAd[a.id];
      /*
       * No row is not zero.
       *
       * An ad with no consolidation rows never delivered; an ad with rows summing to zero
       * delivered nothing. Printing 0 for both makes "we have no data" indistinguishable
       * from "it got no clicks", which is the distinction the whole engine keeps.
       */
      if (!delivery) return <span className="muted">…</span>;
      if (!d) return <span className="muted">—</span>;
      /*
       * Popunder: an impression IS a click. Show its impressions as clicks HERE ONLY — this derivation is
       * page-local and is deliberately not carried into the ad-group total, CTR, or any report.
       */
      if (a.type === 'POPUNDER') {
        return <span className="num" title="Popunder — impressions counted as clicks (this page only)">{int(d.impressions)}<sup className="derived">*</sup></span>;
      }
      return <span className="num">{int(d.clicks)}</span>;
    },
  },
  {
    // Per-ad spend — this is where a POPUNDER's cost is visible (it has impressions + spend, no clicks).
    key: 'spend', label: 'Spend', align: 'right', width: '8rem',
    render: (a) => {
      const d = delivery?.byAd[a.id];
      if (!delivery) return <span className="muted">…</span>;
      return d ? <span className="num">{money(d.spend)}</span> : <span className="muted">—</span>;
    },
  },
  {
    /*
     * What the traffic actually reaches. For a POPUNDER / ADSIMILATE source ad that is the LINK ROTATOR
     * (`…/r/i/<campaignId>`, round-robining the campaign's active clickable creatives) — already authored
     * into its `original_url`. For every other creative it is the advertiser's real landing page
     * (`originalUrl`), NOT `destination_url`, which is only the fraudfree.net tracking/redirect hop.
     */
    key: 'dest', label: 'Destination', render: (a) => {
      const isRotator = ROTATOR_TYPES.has(a.type);
      const url = isRotator ? a.originalUrl : (a.originalUrl || a.destinationUrl);
      if (!url) return <span className="muted">—</span>;
      return (
        <span className="url">
          {isRotator && (
            <span className="pill" title="Link rotator — round-robins across the campaign's active clickable creatives (POPUNDER / ADSIMILATE excluded)">rotator</span>
          )}
          <a href={url} target="_blank" rel="noreferrer noopener" title={url}>{url}</a>
        </span>
      );
    },
  },
  { key: 'active', label: 'Status', width: '6rem',
    render: (a) => <span className={a.isActive ? 'pill ok' : 'pill off'}>{a.isActive ? 'active' : 'inactive'}</span> },
];

/**
 * The delivery figures, extracted so the summary is only reached once it exists.
 *
 * Inlined, the `!delivery` guard did not narrow `delivery?.summary` for the compiler and
 * every field needed a `!`. A component whose prop is non-optional says the same thing to
 * a reader and to the type checker.
 */
function DeliveryFacts({ d }: { d: AdGroupDelivery }) {
  const s = d.summary;
  return (
    <>
      <dl className="facts">
        <div><dt>Clicks</dt><dd className="figure">{int(s.clicks)}</dd></div>
        <div><dt>Impressions</dt><dd className="figure">{int(s.impressions)}</dd></div>
        {s.retargetImpressions > 0 && (
          <div><dt>Retargeted</dt><dd className="figure">{int(s.retargetImpressions)}</dd></div>
        )}
        <div>
          <dt>CTR</dt>
          {/*
            Against CONTRACTED impressions (PAP-371), not delivered — the same rule the
            reports use. Without a contract there is no CTR to state, and an em dash says
            so rather than quietly switching denominator.
          */}
          <dd className="figure">
            {s.targetImpressions && s.targetImpressions > 0
              ? `${((s.clicks / s.targetImpressions) * 100).toFixed(2)}%`
              : '—'}
          </dd>
        </div>
        <div><dt>Contracted</dt><dd className="figure">{int(s.targetImpressions)}</dd></div>
        <div><dt>Days</dt><dd className="figure">{int(s.days)}</dd></div>
      </dl>
      <p className="tiny muted">
        {s.firstDate} → {s.lastDate}
        {d.adTotalsDiffer && (
          <>
            {' · '}
            <strong>Per-ad clicks do not sum to the group total.</strong> Usually an ad
            removed since it delivered; its rows remain and the group figure is authoritative.
          </>
        )}
      </p>
    </>
  );
}

export function AdGroupDetailScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const [adGroup, setAdGroup] = useState<AdGroup | null>(null);
  const [delivery, setDelivery] = useState<AdGroupDelivery | null>(null);
  /*
   * Defaults to every status, not to active.
   *
   * 108 ads have delivered while not marked active (D33), so an "active only" default
   * would hide ads that produced the clicks shown in the summary directly above them.
   */
  const [status, setStatus] = useState<'all' | 'active' | 'inactive'>('all');

  const load = useCallback(
    (p: ListParams) => hierarchy.adGroupAds(id, { ...p, status: status === 'all' ? undefined : status }),
    [id, status]
  );

  useEffect(() => { hierarchy.adGroup(id).then(setAdGroup).catch(() => {}); }, [id]);

  /*
   * The parent campaign, for the breadcrumb.
   *
   * The trail read "Campaigns / Campaign / DISPLAY" — a literal word where a name
   * belongs, which tells a reader nothing about where they are and is indistinguishable
   * from a campaign actually called "Campaign". The ad group carries only `campaignId`,
   * so the name is a second fetch; it arrives after the crumb first paints, which is why
   * the fallback is the neutral word rather than an empty gap that then shifts.
   */
  const [parent, setParent] = useState<Campaign | null>(null);
  useEffect(() => {
    setParent(null);
    if (!adGroup) return;
    let live = true;
    hierarchy.campaign(adGroup.campaignId)
      .then((c) => { if (live) setParent(c); })
      .catch(() => {});
    return () => { live = false; };
  }, [adGroup]);
  useEffect(() => {
    setDelivery(null);
    hierarchy.adGroupDelivery(id).then(setDelivery).catch(() => {});
  }, [id]);

  return (
    <>
      <nav className="crumbs">
        <Link to="/campaigns">Campaigns</Link> <span>/</span>
        {adGroup && (
          <>
            <Link to={`/campaigns/${adGroup.campaignId}`}>{parent?.name ?? 'Campaign'}</Link>
            {' '}<span>/</span>{' '}
          </>
        )}
        <span>{adGroup?.name ?? 'Ad group'}</span>
      </nav>

      {adGroup && (
        <section className="detail">
          <div className="detail-head">
            <h2>{adGroup.name}</h2>
            <div className="row" style={{ gap: '0.5rem' }}>
              <Link className="button" to={`/adgroups/${adGroup.id}/edit`}>Edit</Link>
              <Link className="button primary" to={`/adgroups/${adGroup.id}/ads/new`}>Add ad</Link>
            </div>
          </div>
          <dl className="facts">
            <div><dt>Flight</dt><dd>{adGroup.startDateTime ?? '—'} → {adGroup.endDateTime ?? '—'}</dd></div>
            <div><dt>Bid</dt><dd>{adGroup.bid ?? '—'}</dd></div>
            <div><dt>Daily budget</dt><dd>{adGroup.dailyBudget === null ? '—' : `$${adGroup.dailyBudget}`}</dd></div>
            <div><dt>Geo</dt><dd>{adGroup.geoType ?? '—'}{adGroup.radius ? ` · ${adGroup.radius}mi` : ''}</dd></div>
          </dl>
        </section>
      )}

      {/*
        Delivery, summed across days.
        `consolidations` stores one row per day per level, so every figure here is a sum —
        reading a single row would report one day's delivery as the group's.
      */}
      <section className="detail delivery">
        <h3>Delivery</h3>
        {!delivery ? (
          <p className="muted">Loading delivery…</p>
        ) : delivery.summary.days === 0 ? (
          <p className="muted">This ad group has no recorded delivery.</p>
        ) : (
          <>
            <DeliveryFacts d={delivery} />
            {delivery.byDay.length > 0 && (
              <div style={{ marginTop: '0.75rem' }}>
                <h4 style={{ margin: '0 0 0.35rem', fontSize: '0.9rem' }}>Impressions &amp; clicks by day</h4>
                <DeliveryDayChart byDay={delivery.byDay} />
              </div>
            )}
          </>
        )}
      </section>

      <div className="filter-bar">
        <span className="filter-label">Show</span>
        {(['all', 'active', 'inactive'] as const).map((v) => (
          <button
            key={v}
            className={`chip${status === v ? ' chip-on' : ''}`}
            onClick={() => setStatus(v)}
          >
            {v === 'all' ? 'All' : v === 'active' ? 'Active' : 'Inactive'}
          </button>
        ))}
        <span className="tiny muted">
          Status is what the ad is set to today. An ad can have delivered while inactive.
        </span>
      </div>

      <DataTable
        title="Ads"
        columns={adColumns(delivery)}
        load={load}
        initialSort="date_created"
        searchPlaceholder="Search ads…"
        onRowClick={(a) => navigate(`/ads/${a.id}/edit`)}
        emptyMessage={
          status === 'all' ? 'This ad group has no ads.' : `No ${status} ads in this ad group.`
        }
      />
      <p className="table-note muted">
        <sup className="derived">*</sup> Popunder — an impression is counted as a click. Shown on this page only; it is not carried into the ad-group total, CTR, or reports.
      </p>
    </>
  );
}
